<template>
  <v-container>
    <v-dialog v-model="dialog" width="1039px" persistent :retain-focus="false">
      <v-card class="custom-card" rounded="0">
        <v-toolbar color="primary" class="white--text">
          <v-toolbar-title class="font-weight-bold">
            {{ $t('esgPlatform.selectSurveyUsers') }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon class="white--text" @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="black--text pa-6">
          <p class="mb-4">{{ $t('esgPlatform.pleaseSelectUsersToProceed') }}</p>

          <div class="d-flex align-center mb-5">
            <div class="selected-badge">
              <span class="white--text font-weight-bold">{{ selectedRows.length }}</span>
            </div>
            <div class="title ml-3">{{ selectedRows.length === 1 ? $t('esgPlatform.userSelected') : $t('esgPlatform.usersSelected') }}</div>
            <v-spacer></v-spacer>
            <div class="py-1 title">{{ $t('esgPlatform.conditional') }}</div>
            <v-btn class="mx-2" icon color="primary" @click="openAutoConditions">
              <v-icon dark large>mdi-cog</v-icon>
            </v-btn>
            <div class="py-1 title">{{ $t('esgPlatform.filterBy') }}</div>
            <v-btn class="mx-2" icon color="primary" @click="showFilters = true">
              <v-icon dark large>mdi-filter</v-icon>
            </v-btn>
          </div>

          <v-data-table
            v-model="selectedRows"
            :headers="headers"
            :items="users"
            :loading="loading"
            :loading-text="$t('esgPlatform.loadingUsers')"
            show-select
            item-key="id"
            hide-default-footer
            class="elevation-1"
          >
            <template v-slot:top>
              <div class="d-flex mt-5">
                <v-text-field
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  :placeholder="$t('esgPlatform.searchUsers')"
                  dense
                  v-model="search"
                  @input="debouncedSearch"
                  class="shrink mr-5"
                  style="width: 306px"
                />
                <v-select
                  :items="subOrganizationOptions"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  :placeholder="$t('esgPlatform.subOrganization')"
                  v-model="localFilter.subOrganizationId"
                  class="shrink mr-5"
                  style="width: 250px"
                  @change="applyFilters"
                />
                <v-btn color="primary" small class="mt-1" @click="selectAllVisible">
                  {{ $t('esgPlatform.selectAllUsers') }}
                </v-btn>
                <v-spacer></v-spacer>
                <div class="py-3">{{ from }} - {{ to }} of {{ totalCount }}</div>
                <v-btn class="mx-2" fab small outlined color="primary" :disabled="page <= 1" @click="pageChange(-1)">
                  <v-icon dark>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn class="mx-2" fab small outlined color="primary" :disabled="page >= totalPages" @click="pageChange(1)">
                  <v-icon dark>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </template>

            <template v-slot:item.active="{ item }">
              <span :class="item.active ? 'green-text' : 'red-text'">
                {{ item.active ? $t('esgPlatform.active') : $t('esgPlatform.deactivated') }}
              </span>
            </template>
          </v-data-table>

          <v-divider class="mt-5"></v-divider>
          <v-card-actions class="mt-6 px-0">
            <v-spacer></v-spacer>
            <v-btn outlined class="mr-2" height="44" width="116" depressed color="primary" @click="closeModal">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn height="44" width="116" color="primary" :disabled="!selectedRows.length" @click="saveSelectedUsers">
              {{ $t('esgPlatform.continue') }}
            </v-btn>
          </v-card-actions>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showFilters" width="356" content-class="esg-assessment-filter-dialog" transition="dialog-bottom-transition">
      <v-card min-height="100vh" rounded="0">
        <v-toolbar color="primary" class="white--text" flat :elevation="0">
          <v-toolbar-title class="font-weight-bold">{{ showAutoCondition ? $t('esgPlatform.autoConditions') : $t('esgPlatform.filterBy') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon class="white--text" @click="closeFilter">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-5">
          <v-row no-gutters>
            <v-col cols="12">
              <label class="text-subtitle-1 font-weight-bold">{{ $t('esgPlatform.membershipType') }}</label>
              <v-select :items="membershipTypeOptions" item-text="label" item-value="value" outlined dense placeholder="Select" v-model="localFilter.membership_type" />
            </v-col>

            <v-col cols="12" v-if="!showAutoCondition">
              <label class="text-subtitle-1 font-weight-bold">{{ $t('esgPlatform.memberRole') }}</label>
              <v-select :items="memberRoleOptions" item-text="label" item-value="value" outlined dense placeholder="Select" v-model="localFilter.membership_role" />
            </v-col>

            <v-col cols="12" v-if="!showAutoCondition">
              <label class="text-subtitle-1 font-weight-bold">{{ $t('esgPlatform.gender') }}</label>
              <v-select :items="genderOptions" item-text="label" item-value="value" outlined dense placeholder="Select" v-model="localFilter.gender" />
            </v-col>

            <v-col cols="12" v-if="!showAutoCondition">
              <label class="text-subtitle-1 font-weight-bold">Country</label>
              <v-autocomplete
                outlined
                dense
                v-model="localFilter.country"
                :items="countries"
                item-value="name"
                item-text="name"
                placeholder="Select"
                return-object
                @change="getState"
              />
            </v-col>

            <v-col cols="12" v-if="!showAutoCondition">
              <label class="text-subtitle-1 font-weight-bold">
                {{ $t(`${isKenyaClient() ? "County" : "provinceState"}`) }}
              </label>
              <v-autocomplete
                outlined
                dense
                v-model="localFilter.stateId"
                :items="states"
                :filter="filterState"
                :placeholder="$t(`${isKenyaClient() ? 'County' : 'provinceState'}`)"
                return-object
              />
            </v-col>

            <v-col cols="12" v-if="!showAutoCondition">
              <label class="text-subtitle-1 font-weight-bold">{{ $t('esgPlatform.registrationDate') }}</label>
              <v-menu v-model="openStartDate" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field append-icon="mdi-calendar" outlined dense readonly clearable :placeholder="$t('esgPlatform.startDateLabel')" v-model="localFilter.startDate" v-bind="attrs" v-on="on" @click:clear="localFilter.startDate = ''" />
                </template>
                <v-date-picker v-model="localFilter.startDate" @input="openStartDate = false" />
              </v-menu>
              <v-menu v-model="openEndDate" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field append-icon="mdi-calendar" outlined dense readonly clearable :placeholder="$t('esgPlatform.endDateLabel')" v-model="localFilter.endDate" v-bind="attrs" v-on="on" @click:clear="localFilter.endDate = ''" />
                </template>
                <v-date-picker v-model="localFilter.endDate" @input="openEndDate = false" />
              </v-menu>
            </v-col>

            <v-col cols="12" v-if="showAutoCondition">
              <label class="text-subtitle-1 font-weight-bold">{{ $t('esgPlatform.conditionStatus') }}</label>
              <div>{{ $t('esgPlatform.conditionDescription') }}</div>
              <div class="d-flex align-center justify-space-around">
                <span class="active-label">{{ $t('esgPlatform.active') }}</span>
                <v-switch inset v-model="autoConditionState.active"></v-switch>
              </div>
            </v-col>
          </v-row>

          <v-row class="mt-5">
            <v-col cols="12" class="d-flex justify-space-around">
              <v-btn outlined width="130" color="primary" @click="clearFilters">{{ $t('esgPlatform.clearAll') }}</v-btn>
              <v-btn width="130" color="primary" @click="showAutoCondition ? saveAutoCondition() : applyFilters()">
                {{ $t('esgPlatform.save') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import debounce from "lodash/debounce";
import EsgService from "@/_services/EsgService";
import { getCountries, getStates } from "country-state-picker";
import { kenyaCounties } from "@/constants/countries.js";
import { isKenyaClient } from "@/utils";

const getDefaultFilter = () => ({
  membership_type: "",
  membership_role: "",
  gender: "",
  country: "",
  stateId: "",
  startDate: "",
  endDate: "",
  subOrganizationId: "all",
});

const normalizeSurveyUserFilter = (filter = {}) => {
  const defaults = getDefaultFilter();
  return Object.keys(defaults).reduce((normalizedFilter, key) => ({
    ...normalizedFilter,
    [key]: filter[key] ?? defaults[key],
  }), {});
};

const normalizeSurveyUserRequestFilter = (filter = {}) => {
  const normalizedFilter = normalizeSurveyUserFilter(filter);
  return {
    ...normalizedFilter,
    country: typeof normalizedFilter.country === "object" && normalizedFilter.country !== null
      ? normalizedFilter.country.name
      : normalizedFilter.country,
  };
};

const normalizeAutoCondition = (autoCondition = {}) => ({
  active: Boolean(autoCondition.active),
});

export default {
  props: {
    dialogI: {
      type: Boolean,
      required: true,
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    selectAll: {
      type: Boolean,
      default: false,
    },
    initialFilter: {
      type: Object,
      default: () => ({}),
    },
    autoCondition: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      users: [],
      selectedRows: [],
      countries: [],
      states: [],
      subOrganizationOptions: [{ id: "all", name: this.$t('esgPlatform.all') }],
      page: 1,
      limit: 5,
      totalCount: 0,
      totalPages: 1,
      search: "",
      loading: false,
      showFilters: false,
      showAutoCondition: false,
      openStartDate: false,
      openEndDate: false,
      autoConditionState: normalizeAutoCondition(),
      membershipTypeOptions: [
        { label: this.$t('esgPlatform.membershipTypeDefault'), value: "default" },
        { label: this.$t('esgPlatform.farmer'), value: "farmer" },
        { label: this.$t('esgPlatform.technician'), value: "technician" },
        { label: this.$t('esgPlatform.factoryBuyingStation'), value: "factory_buying_station" },
      ],
      farmerRoleOptions: [
        { label: this.$t('esgPlatform.farmer'), value: "farmer" },
        { label: this.$t('esgPlatform.coffeeFarmer'), value: "coffee_farmer" },
        { label: this.$t('esgPlatform.cacaoFarmer'), value: "cacao_farmer" },
      ],
      genderOptions: [
        { label: this.$t('esgPlatform.male'), value: "male" },
        { label: this.$t('esgPlatform.female'), value: "female" },
        { label: this.$t('esgPlatform.other'), value: "other" },
      ],
      localFilter: getDefaultFilter(),
      headers: [
        { text: this.$t('esgPlatform.userName'), align: "start", value: "fullName" },
        { text: this.$t('esgPlatform.email'), align: "start", value: "email" },
        { text: this.$t('esgPlatform.subOrganization'), align: "start", value: "subOrganizationName" },
        { text: this.$t('esgPlatform.membershipType'), align: "start", value: "membershipType" },
        { text: this.$t('status'), align: "center", value: "active" },
      ],
    };
  },
  computed: {
    dialog() {
      return this.dialogI;
    },
    from() {
      return this.totalCount ? (this.page - 1) * this.limit + 1 : 0;
    },
    to() {
      return Math.min(this.page * this.limit, this.totalCount);
    },
    memberRoleOptions() {
      return this.farmerRoleOptions;
    },
  },
  mounted() {
    this.getCountry();
    this.localFilter = this.normalizeLocalFilter(this.initialFilter);
    this.setStatesForCountry(this.localFilter.country);
    this.autoConditionState = normalizeAutoCondition(this.autoCondition);
    this.loadSubOrganizations();
    this.loadUsers();
  },
  methods: {
    getCountry() {
      this.countries = getCountries();
    },
    setStatesForCountry(country) {
      if (!country || !country.code) {
        this.states = [];
        return;
      }

      this.states = country.code.toLowerCase() === "ke"
        ? kenyaCounties
        : getStates(country.code);
    },
    getState(val) {
      this.setStatesForCountry(val);
      this.localFilter.stateId = "";
    },
    normalizeLocalFilter(filter = {}) {
      const normalizedFilter = normalizeSurveyUserFilter(filter);
      const countryName = typeof normalizedFilter.country === "object" && normalizedFilter.country !== null
        ? normalizedFilter.country.name
        : normalizedFilter.country;

      return {
        ...normalizedFilter,
        country: (this.countries || []).find((item) => item.name === countryName) || "",
      };
    },
    filterState(item, queryText) {
      return item.toLocaleLowerCase().includes(queryText.toLocaleLowerCase());
    },
    isKenyaClient() {
      return isKenyaClient();
    },
    async loadSubOrganizations() {
      try {
        const response = await EsgService.getAssessmentSubOrganizations({ page: 1, limit: 100 });
        const rows = response.data.rows || [];
        this.subOrganizationOptions = [
          { id: "all", name: this.$t('esgPlatform.all') },
          ...rows.map((item) => ({ id: item.cfOrgId, name: item.name })).filter((item) => item.id),
        ];
      } catch (err) {
        this.subOrganizationOptions = [{ id: "all", name: this.$t('esgPlatform.all') }];
      }
    },
    async loadUsers() {
      this.loading = true;
      try {
        this.clearUnsupportedRoleFilter();
        const response = await EsgService.getAssessmentSurveyUsers({
          page: this.page,
          limit: this.limit,
          search: this.search,
          ...normalizeSurveyUserRequestFilter(this.localFilter),
        });
        this.users = response.data.rows || [];
        this.totalCount = response.data.count || 0;
        this.totalPages = response.data.totalPages || 1;
        const selectedIdSet = new Set(this.selectedIds);
        this.selectedRows = [
          ...this.selectedRows,
          ...this.users.filter((item) => selectedIdSet.has(item.mongoId)),
        ].filter((item, index, arr) => arr.findIndex((selected) => selected.mongoId === item.mongoId) === index);
      } catch (err) {
        this.$notify({ title: this.$t('esgPlatform.failedToLoadSurveyUsers'), type: "error" });
      } finally {
        this.loading = false;
      }
    },
    debouncedSearch: debounce(function () {
      this.page = 1;
      this.loadUsers();
    }, 500),
    pageChange(direction) {
      this.page += direction;
      this.loadUsers();
    },
    selectAllVisible() {
      this.selectedRows = [
        ...this.selectedRows,
        ...this.users,
      ].filter((item, index, arr) => arr.findIndex((selected) => selected.id === item.id) === index);
    },
    openAutoConditions() {
      this.showAutoCondition = true;
      this.showFilters = true;
    },
    closeFilter() {
      this.showFilters = false;
      this.showAutoCondition = false;
    },
    clearFilters() {
      this.localFilter = getDefaultFilter();
      this.states = [];
      this.autoConditionState = normalizeAutoCondition();
      this.page = 1;
      this.closeFilter();
      this.loadUsers();
    },
    applyFilters() {
      this.page = 1;
      this.closeFilter();
      this.loadUsers();
    },
    saveAutoCondition() {
      this.closeFilter();
    },
    clearUnsupportedRoleFilter() {
      if (
        this.localFilter.membership_role
        && !this.memberRoleOptions.some((option) => option.value === this.localFilter.membership_role)
      ) {
        this.localFilter.membership_role = "";
      }
    },
    closeModal() {
      this.$emit("closeDialog");
    },
    saveSelectedUsers() {
      this.clearUnsupportedRoleFilter();
      this.$emit("usersSelected", {
        selected: this.selectedRows,
        selectAll: this.selectAll,
        filter: normalizeSurveyUserRequestFilter(this.localFilter),
        autoCondition: normalizeAutoCondition(this.autoConditionState),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.selected-badge {
  width: 31px;
  height: 31px;
  background: #00bd73;
  display: flex;
  border-radius: 20px;
  align-items: center;
  justify-content: center;
}

.green-text {
  color: #006400;
}

.red-text {
  color: #ff0000;
}


.active-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: 0.67px;
  color: #000000;
}
</style>

<style lang="scss">
.esg-assessment-filter-dialog {
  position: absolute;
  top: 0;
  min-height: 100%;
  right: 0;
  margin-right: 0;
  margin-top: 0;
}
</style>
